/**
 * @param char
 *
 * @returns
 * The numeric value for the given char, where:
 * A or a = 10
 * Z or z = 35
 */
function getNumericValue(char: string): number {
  // latin unicode letters A-Z are consecutive
  const diff = char.toUpperCase().charCodeAt(0) - 'A'.charCodeAt(0);
  return 10 + diff;
}

/**
 * @param str The string to group
 * @param size The maximum group size
 *
 * @returns
 * An array of strings where each item has a maximum length of `size`.
 */
function getGrouped(str: string, size: number): string[] {
  if (size === 0) {
    throw new Error('groupSize must be bigger than 0');
  }

  const result = [];
  const { length } = str;

  for (
    let start = 0, end = start + size;
    start < length;
    start = end, end = start + size
  ) {
    result.push(str.slice(start, end));
  }

  return result;
}

/**
 * Represents an IBAN
 */
export class Iban {
  /**
   * IBAN in electronic format (without separators)
   */
  private _value: string;

  /**
   * @param iban IBAN represented as a string in electronic format
   */
  constructor(iban: string) {
    this._value = iban;
  }

  /**
   * @param iban IBAN in print format
   *
   * @returns
   * Instance of IBAN from a parsed print format
   */
  public static parse(iban: string): Iban {
    // Remove separators
    return new Iban(iban.replace(/[^A-Za-z0-9]/g, ''));
  }

  /**
   * The IBAN as a string
   */
  public get value() {
    return this._value;
  }

  /**
   * @returns
   * The ISO 3166-1 alpha-2 country code
   */
  public get countryCode() {
    return this.value.substring(0, 2).toUpperCase();
  }

  /**
   * @returns
   * The check digits
   */
  public get checkDigits() {
    return this.value.substring(2, 4);
  }

  /**
   * @returns
   * The Basic Bank Account Number (BBAN)
   */
  public get bban(): string {
    return this.value.substring(4).toUpperCase();
  }

  /**
   * @param separator Separator between groups, defaults to ' '
   *
   * @returns
   * IBAN as string, grouped in groups of 4
   */
  public format(separator: string = ' '): string {
    return getGrouped(this.value, 4).join(separator);
  }

  /**
   * @returns
   * `true`, if the check digits are valid
   */
  public checkDigitsValid(): boolean {
    const d = this.getReorderedNumberRepresentation();

    // get the first 2 digits
    let n = Number.parseInt(d.substring(0, 2), 10);
    // and group the rest in groups of 7
    const groups = getGrouped(d.substring(2), 7);

    for (const group of groups) {
      n = Number.parseInt(`${n}${group}`, 10) % 97;
    }

    return n === 1;
  }

  private getReorderedNumberRepresentation(): string {
    // reorder iban
    const { countryCode, checkDigits, bban } = this;
    const reordered = `${bban}${countryCode}${checkDigits}`;

    // replace letters with their numeric values
    return reordered.replace(/[A-Z]/g, (char) =>
      getNumericValue(char).toString(),
    );
  }
}
